/* src/components/testing/countingSpan/RecallInterface.js */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Provides the user interface for recalling the sequence of counts.
 * Displays a number pad and input fields for the user to enter their response.
 * Acts as a controlled component, receiving the sequence via `value` and
 * reporting changes via `onChange`.
 *
 * @param {object} props
 * @param {number} props.setSize - The number of items to recall for the current set.
 * @param {function} props.onSubmit - Function to call when the user submits the sequence: (sequence) => void.
 * @param {Array<number|null>} props.value - The current recall sequence state from the parent hook.
 * @param {function} props.onChange - Function to call when the sequence is modified by the user: (newSequence) => void.
 */
const RecallInterface = ({ setSize, onSubmit, value, onChange }) => {
    // Internal state primarily for managing the visual focus/cursor position
    const [currentInputIndex, setCurrentInputIndex] = useState(0);

    // Use the sequence passed from the parent hook via 'value' prop.
    // Default to an array of nulls if the value prop isn't provided initially.
    const recalledSequence = value || Array(setSize).fill(null);

    // Effect to update the internal cursor position based on the external `value` prop.
    // This handles cases where the parent resets the sequence (e.g., new set)
    // or when the sequence is updated by keyboard/button input.
    useEffect(() => {
        // Find the first null index in the sequence provided by the parent.
        // This is where the next input should logically go.
        const firstNull = recalledSequence.findIndex(val => val === null || val === undefined);
        // If all slots are filled (firstNull is -1), the cursor should be at the end (index setSize).
        // Otherwise, the cursor should be at the index of the first empty slot.
        setCurrentInputIndex(firstNull === -1 ? setSize : firstNull);
    }, [recalledSequence, setSize]); // Re-run whenever the sequence value or setSize changes


    // --- Event Handlers ---

    // Handle number button clicks (or keyboard number input)
    const handleNumberClick = useCallback((number) => {
        // Use the `currentInputIndex` (derived from the `value` prop) to determine where to insert.
        // This ensures we fill the first available slot.
        const targetIndex = currentInputIndex;

        // Only proceed if the target index is within the bounds of the set size.
        if (targetIndex < setSize) {
            // Create a copy of the sequence from the `value` prop to modify.
            const newSequence = [...recalledSequence];
            // Update the sequence at the target index.
            newSequence[targetIndex] = number;
            // Notify the parent component of the change via the `onChange` callback.
            if (onChange) {
                onChange(newSequence);
            }
            // The cursor position (`currentInputIndex`) will update automatically
            // in the `useEffect` hook when the `value` prop changes.
        }
    }, [currentInputIndex, setSize, recalledSequence, onChange]); // Dependencies

    // Handle backspace button click (or keyboard backspace)
    const handleBackspaceClick = useCallback(() => {
        // Determine the index to clear. If the cursor is at the end (`setSize`),
        // clear the last element (`setSize - 1`). Otherwise, clear the element
        // *before* the current cursor position (`currentInputIndex - 1`).
        const indexToClear = (currentInputIndex === setSize) ? setSize - 1 : currentInputIndex - 1;

        // Only proceed if the index to clear is valid (0 or greater).
        if (indexToClear >= 0) {
            // Create a copy of the sequence to modify.
            const newSequence = [...recalledSequence];
            // Clear the value at the determined index.
            newSequence[indexToClear] = null;
            // Notify the parent of the change.
            if (onChange) {
                onChange(newSequence);
            }
            // The cursor position will update automatically via the `useEffect`.
        }
    }, [currentInputIndex, setSize, recalledSequence, onChange]); // Dependencies

    // Handle submitting the sequence via the Submit button (or Enter key)
    const handleSubmitClick = useCallback(() => {
        // Pass the current sequence state (from the `value` prop) to the parent's onSubmit handler.
        onSubmit(recalledSequence);
    }, [onSubmit, recalledSequence]); // Dependencies

    // Effect to handle keyboard input for numbers, backspace, and enter
    useEffect(() => {
        const handleKeyDown = (event) => {
            const key = event.key;
            // Handle number input (0-9)
            if (!isNaN(parseInt(key)) && key.length === 1) {
                event.preventDefault(); // Prevent default browser actions (e.g., typing in other fields)
                handleNumberClick(parseInt(key));
            }
            // Handle Backspace
            else if (key === 'Backspace') {
                event.preventDefault();
                handleBackspaceClick();
            }
            // Handle Enter for submission
            else if (key === 'Enter') {
                event.preventDefault();
                // Submit the current sequence (state is managed by parent via `value`)
                 handleSubmitClick();
            }
        };

        // Add event listener when the component mounts
        window.addEventListener('keydown', handleKeyDown);
        // Remove event listener when the component unmounts
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleNumberClick, handleBackspaceClick, handleSubmitClick]); // Rerun if handler references change


    // --- Rendering ---

    const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0]; // Order for keypad layout

    return (
        <div className="flex flex-col items-center w-full max-w-xs mx-auto my-4">
            <p className="mb-3 text-lg font-medium text-gray-700">Recall the counts in order:</p>

            {/* Input Display Area - Renders based on `recalledSequence` from props */}
            <div className="flex justify-center space-x-2 mb-4 h-12 items-end">
                {/* Always render placeholders based on setSize */}
                {Array.from({ length: setSize }).map((_, index) => (
                    <span
                        key={index}
                        className={`w-8 pb-1 text-center text-2xl font-semibold border-b-2 transition-colors duration-100 ${
                            // Highlight the current input position (cursor) with a pulse animation
                            index === currentInputIndex && currentInputIndex < setSize
                                ? 'border-indigo-500 animate-pulse'
                                : 'border-gray-300'
                        } ${
                            // Style filled slots differently from empty slots
                            recalledSequence[index] !== null && recalledSequence[index] !== undefined
                                ? 'text-gray-900' // Filled slot text color
                                : 'text-gray-400' // Empty slot ('_') text color
                        }`}
                    >
                        {/* Display the number if present, otherwise display underscore */}
                        {recalledSequence[index] !== null && recalledSequence[index] !== undefined
                            ? recalledSequence[index]
                            : '_'}
                    </span>
                ))}
            </div>

            {/* Number Pad */}
            <div className="grid grid-cols-3 gap-2 mb-3 w-full">
                {numbers.map((num) => (
                    <button
                        key={num}
                        onClick={() => handleNumberClick(num)}
                        // Disable number buttons if the cursor is already past the last input slot
                        disabled={currentInputIndex >= setSize}
                        className={`py-3 px-4 text-xl font-medium rounded-md border shadow-sm transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 ${
                            currentInputIndex >= setSize
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' // Disabled style
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50' // Enabled style
                        }`}
                    >
                        {num}
                    </button>
                ))}
            </div>

            {/* Control Buttons (Backspace and Submit) */}
             <div className="grid grid-cols-2 gap-2 w-full">
                {/* Backspace Button */}
                <button
                    onClick={handleBackspaceClick}
                    // Disable Backspace if the cursor is at the very beginning (index 0)
                    disabled={currentInputIndex === 0}
                    className={`py-3 px-4 text-base font-medium rounded-md border shadow-sm transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-amber-500 ${
                        currentInputIndex === 0
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' // Disabled style
                            : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200' // Enabled style
                    }`}
                >
                    Backspace
                </button>
                {/* Submit Button - Always enabled */}
                 <button
                    onClick={handleSubmitClick}
                    className="py-3 px-4 text-base font-medium rounded-md border shadow-sm transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500 bg-green-600 text-white border-green-700 hover:bg-green-700"
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

// PropTypes validation for the component's props
RecallInterface.propTypes = {
    setSize: PropTypes.number.isRequired,
    onSubmit: PropTypes.func.isRequired,
    // `value` should be an array containing numbers or nulls
    value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])])).isRequired,
    // `onChange` is the callback function to update the parent's state
    onChange: PropTypes.func.isRequired,
};

export default RecallInterface;